/**
 * The marks on the text.
 *
 * Two kinds, separated for one reason:
 *
 *   - A wrongly written WORD gets a wave. The CSS Custom Highlight API draws
 *     it, and it does not touch the text — caret and undo stay intact.
 *   - A missing PLACE gets a wedge. The Highlight API cannot do that: it
 *     colours text, and a place between two letters has no width. Every
 *     attempt colours a neighbouring letter that is perfectly correct.
 *
 * The division into buckets is pure logic and therefore stands for itself — it
 * can be checked without a browser having to lay out text. Only the measuring
 * and setting below needs the DOM.
 */

import { rangeFor } from './text.js'
import type { Finding, Insertion } from '../types.js'

/** A word lighting up green: start and end in the text. */
export interface Solved {
  start: number
  end: number
}

/** Which bucket a finding belongs in. */
export interface Buckets {
  /** Wrongly written words — red wave. */
  errors: Finding[]
  /** The word whose help is open. */
  active: Finding[]
  /**
   * Just put right — green lighting up.
   *
   * As a range and not as a finding: as soon as the word stands there
   * correctly, the checking no longer finds it, and it drops out of `errors`.
   * If the green hung on a finding, it would be gone exactly at the moment at
   * which it shall appear — the confirmation for a word put right would stay
   * away for good.
   */
  solved: Solved[]
  /** Comma findings with the counting switched off — quietly dotted. */
  commas: Finding[]
  /** Missing places — wedges in a layer of their own. */
  gaps: Finding[]
}

/**
 * Divides the findings up.
 *
 * A missing comma or space is marked at the PLACE, not on the words.
 *
 * LanguageTool marks the surrounding words — "ablenkt den" for a missing
 * comma, "Aber" in "nicht.Aber" for a missing space. But those words are
 * written perfectly correctly, and a wave underneath them says "these are
 * wrong", which is the one thing they are not. What is missing is one
 * character in between.
 */
export function bucket(
  findings: readonly Finding[],
  opts: {
    activeStart: number | null
    /** The solved ranges, by their start. */
    solved: ReadonlyMap<number, Solved>
    commas: boolean
    insertionAt: (f: Finding) => Insertion | null
    isCommaFinding: (f: Finding) => boolean
  }
): Buckets {
  const out: Buckets = {
    errors: [], active: [], commas: [], gaps: [],
    // The solved ones stand for themselves, independent of the findings.
    solved: [...opts.solved.values()],
  }

  for (const e of findings) {
    // What is lighting up green right now gets no second mark.
    if (opts.solved.has(e.start)) continue
    if (opts.insertionAt(e)) {
      out.gaps.push(e)
      continue
    }
    if (opts.activeStart === e.start) out.active.push(e)
    // With the commas switched off, they keep their mark but in a quiet
    // color — not gone, only not shouting. The help behind them stays
    // reachable by right-click.
    else if (!opts.commas && opts.isCommaFinding(e)) out.commas.push(e)
    else out.errors.push(e)
  }
  return out
}

/**
 * Hands the waves over to the Highlight API.
 *
 * The marks are NOT written into the DOM but handed to the Highlight API
 * as ranges. That is the decisive difference from the earlier overlay: the
 * text node stays untouched, so caret and undo history stay intact — an
 * `innerHTML` here would destroy both.
 */
export function paintHighlights(input: HTMLElement, b: Buckets): void {
  if (!CSS.highlights) return
  const ranges = (list: readonly { start: number; end: number }[]): Range[] =>
    list.map((e) => rangeFor(input, e.start, e.end))

  CSS.highlights.set('kritzl-error', new Highlight(...ranges(b.errors)))
  CSS.highlights.set('kritzl-active', new Highlight(...ranges(b.active)))
  CSS.highlights.set('kritzl-solved', new Highlight(...ranges(b.solved)))
  CSS.highlights.set('kritzl-comma', new Highlight(...ranges(b.commas)))
}

/** Where a wedge belongs, in the coordinates of the wedge layer. */
export interface Wedge {
  left: number
  top: number
  quiet: boolean
}

/**
 * Measures the wedge positions.
 *
 * The position comes from the range of the gap: collapsed at the seam, it
 * delivers a rectangle of zero width at exactly the right place.
 *
 * The measuring cannot be done declaratively: the place is only fixed once
 * the text is laid out. Therefore this function delivers only numbers — the
 * drawing from them happens in Preact.
 */
export function measureWedges(
  input: HTMLElement,
  layer: HTMLElement,
  gaps: readonly Finding[],
  opts: {
    /** The zoom from fitSheet. See below why it is needed here. */
    scale: number
    fontPx: number
    fontFamily: string
    commas: boolean
    insertionAt: (f: Finding) => Insertion | null
    isCommaFinding: (f: Finding) => boolean
  }
): Wedge[] {
  if (!gaps.length) return []
  const base = layer.getBoundingClientRect()

  // The baseline within the line box, from the font metrics.
  //
  // getBoundingClientRect() delivers the LINE BOX — with the line spacing of
  // the paper that reaches far below the letters, and a wedge placed there
  // would sit under the next line. The tip belongs where the letters end, so
  // the ascent is measured and added to the top edge.
  const ctx = document.createElement('canvas').getContext('2d')
  if (!ctx) return []
  ctx.font = `${opts.fontPx}px "${opts.fontFamily}", system-ui`
  const m = ctx.measureText('Hxgp')
  const asc = m.fontBoundingBoxAscent
  const desc = m.fontBoundingBoxDescent

  const out: Wedge[] = []
  for (const e of gaps) {
    const place = opts.insertionAt(e)
    if (!place) continue
    const at = e.start + place.offset
    const box = rangeFor(input, at, at).getBoundingClientRect()
    // A collapsed range in an empty line delivers nothing usable.
    if (!box.height) continue

    // The letters sit centred in the line box; from its middle the ascent
    // reaches up and the descent down.
    // asc/desc come from the font metrics and are NOT scaled; box is. So
    // the ascent is scaled to the same system before it is added.
    const middle = box.top + box.height / 2
    const baseline = middle + ((asc - desc) / 2) * opts.scale

    // On the phone the sheet is scaled down (fitSheet). getBoundingClientRect()
    // then delivers the SCALED pixels, but `left` and `top` of the wedge are
    // set in the unscaled coordinate system of the layer — zoom would apply
    // them a second time. Dividing by the factor undoes exactly that.
    out.push({
      left: (box.left - base.left) / opts.scale,
      top: (baseline - base.top) / opts.scale + 1,
      // With the commas switched off, a comma wedge goes quiet as well — the
      // same weighting as for the waves. A missing space stays red: it is not
      // a question of style.
      quiet: !opts.commas && opts.isCommaFinding(e),
    })
  }
  return out
}
